/* eslint-disable max-len */
import { AnimatePresence, motion, Variants } from 'framer-motion';
import { darken } from 'polished';
import React from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'wouter';
import { useObserver } from '../../../CodesThatsGottaGoSomewhereElse';
import { Icon } from '../../../concepts/resources/Icon';
import { Colors } from '../../../ui';
import { applyFilter, applySort, SortMode } from '../../common/Sorter';
import { FolderModel } from '../Models';
import { BoardThumb } from './BoardThumb';
import { FolderActionMenu } from './FolderActionMenu';
import { Flexer } from './LayoutElements';

const IconSvg = styled.svg`
    width: 3rem;
    margin: .5rem 1rem .5rem 0 ;
`;

const FolderFrame = styled(motion.div)`
    position: relative;   
    height: 12rem;
    margin-bottom: 1rem;
`;

const FolderActions = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    color: ${Colors.textLight};
`;

const FolderListWrapper = styled.div<{ gridLike?: boolean }>`
    ${p => !p.gridLike ? '' : css`
        margin: -1rem -1rem 0;
        ${FolderFrame} {
            display: inline-block;
            margin: 1rem;
            width: 20rem;
        }
    `}
`;

const FolderBorder = styled.div`
    position: relative;        
    height: 100%;
    display: flex; 
    flex-direction: column;
    align-items: flex-start;
    padding: 1rem 1.5rem;
    border: solid .125rem #eee;
    border-radius: .25rem;
    background: #FFFFFF;

    &:hover {
        cursor: pointer;
        box-shadow: 0 0 0 .125rem #eee;
    }

    &:active {
        cursor: pointer;
        border: solid .125rem ${darken(.05, '#eee')};
        box-shadow: 0 0 0 .125rem ${darken(.05, '#eee')};
    }
`;

const FolderInfo = styled.div`
    width: 16rem;
`;

const FolderLabel = styled.div`
    max-height: 3rem;   
    font-size: 1.125rem;
    font-weight: 400;
    color: ${Colors.text};
    margin-top: .25em;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
`;

const FolderDesc = styled.div`
    font-size: .875rem;
    color: ${Colors.text};
    margin-top: .25em;
`;

const FolderItems = styled.div`
    position: absolute;
    top: 0;
    left: 18rem;
    bottom: 0;
    right: 0;
    overflow: hidden;
    pointer-events: none;
`;

const FolderItemLabel = styled.div`
    display: none;
    position: absolute;
    padding: .25rem .5rem;
    left: 0;
    right: 0;
    bottom: 0;
    background: #d2e1ec;
    color: ${Colors.text};
    font-size: .75rem;
    font-weight: 400;
    border-top: solid .125rem #d2e1ec;
`;

const FolderItemBorder = styled.div`
    position: relative;
    display: inline-block;
    width: 13.25rem;
    height: 10rem;
    margin: 1rem;
    background: gainsboro;
    pointer-events: all;
    border-radius: .125rem;
    border: solid .125rem #d2e1ec;
    vertical-align: top;
    
    & > img {
        border-radius: .125rem;
        max-height: 100%;
    }

    /* &:after {
        content: '';
        position: absolute;
        left: -1px;
        top: -1px;
        right: -1px;
        bottom: -1px;
        /* border-radius: .25rem; 
    }*/

    &:hover {
        cursor: pointer;
        box-shadow: 0 0 0 .125rem #d2e1ec;

        ${FolderItemLabel} {
            display: block;
        }
    }

    &:active {
        cursor: pointer;
        border: solid .125rem ${darken(.05, '#d2e1ec')};
        box-shadow: 0 0 0 .125rem ${darken(.05, '#d2e1ec')};
    }
`;

export type FolderListProps = {
    folders: readonly FolderModel[],
    sort: SortMode,
    filter: string,
    gridLike?: boolean,
};

const folderAnim: Variants = {
    before: {
        opacity: 0,
        scale: .5
    },
    during: {
        opacity: 1,
        scale: 1.0,
        transition: {
            opacity: { duration: .4, ease: 'linear', },
            scale: { duration: .5, ease: [0, .99, .2, 1.14], },
        }
    },
    after: {
        opacity: 0,
        scale: .5,
        transition: { ease: [.3, 1, .3, 1], duration: .5 }
    }
};

const FolderListItem = React.memo<{ folder: FolderModel, gridLike: boolean }>(({ folder, gridLike }) => {
    const { label, icon, path, special, boards } = useObserver(folder);

    return <FolderFrame variants={folderAnim} initial="before" animate="during" exit="after">
        <Link to={path}>
            <FolderBorder>
                <Icon size={4} src={!!icon ? `${icon}@[0.9,0.9,0]` : 'deqo://icons/dash/folder-grid-icon'} />
                <FolderInfo>
                    <FolderLabel>{label}</FolderLabel>
                    <FolderDesc>{boards.length} Boards</FolderDesc>
                </FolderInfo>
            </FolderBorder>
        </Link>
        {!gridLike &&
            <FolderItems>
                {boards.map(b =>
                    <Link key={b.id} to={b.path}>
                        <FolderItemBorder>
                            <BoardThumb info={b} />
                            <FolderItemLabel>{b.label}</FolderItemLabel>
                        </FolderItemBorder>
                    </Link>
                )}
            </FolderItems>}
        {!special && <FolderActions>
            <Flexer padding=".5">
                <FolderActionMenu folder={folder} />
            </Flexer>
        </FolderActions>}
    </FolderFrame>;
});

export const FolderList = React.memo<FolderListProps>(function FolderList({ gridLike, folders, sort, filter }) {

    const reduced = applySort(applyFilter(folders, filter), sort);

    return <FolderListWrapper gridLike={gridLike}>
        <AnimatePresence initial={false}>
            {reduced.map(f => <FolderListItem key={f.id} folder={f} gridLike={gridLike} />)}
        </AnimatePresence>
    </FolderListWrapper>;
});
